import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { authService } from '../../utils/authService';
import Header from '../../components/ui/Header';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import Captcha from '../../components/ui/Captcha';
import { Eye, EyeOff, Mail, Lock, User, AlertCircle, CheckCircle } from 'lucide-react';

const Signup = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    companyName: '',
    email: '',
    password: '',
    confirmPassword: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [captchaToken, setCaptchaToken] = useState(null);
  const [captchaError, setCaptchaError] = useState('');
  const [captchaReset, setCaptchaReset] = useState(false);
  const navigate = useNavigate();

  const handleInputChange = (e) => {
    const { name, value } = e?.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    setError(''); // Clear error when user starts typing
    setCaptchaError(''); // Clear captcha error when user starts typing
  };

  const handleCaptchaVerify = (token, error) => {
    if (error) {
      setCaptchaError(error);
      setCaptchaToken(null);
    } else {
      setCaptchaToken(token);
      setCaptchaError('');
    }
  };

  const validateForm = () => {
    if (!formData?.firstName?.trim()) {
      return 'First name is required';
    }
    if (!formData?.lastName?.trim()) {
      return 'Last name is required';
    }
    if (!formData?.companyName?.trim()) {
      return 'Company name is required';
    }
    if (!formData?.email?.trim()) {
      return 'Email is required';
    }
    if (!formData?.password) {
      return 'Password is required';
    }
    if (formData?.password?.length < 6) {
      return 'Password must be at least 6 characters long';
    }
    if (formData?.password !== formData?.confirmPassword) {
      return 'Passwords do not match';
    }
    if (!captchaToken) {
      return 'Please complete the CAPTCHA verification';
    }
    return null;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }

    setLoading(true);
    setError('');
    setCaptchaError('');

    try {
      const { data, error } = await authService?.signUp(
        formData?.email,
        formData?.password,
        { 
          companyName: formData?.companyName, 
          firstName: formData?.firstName,
          lastName: formData?.lastName,
          role: 'user',
          captchaToken: captchaToken
        }
      );

      if (error) {
        setError(error?.message);
      } else {
        setSuccess(true);
        setTimeout(() => {
          navigate('/auth/login');
        }, 3000);
      }
    } catch (error) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background via-background to-muted/20 relative overflow-hidden">
        {/* Background Pattern */}
        <div 
          className="absolute inset-0 opacity-50" 
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%239C92AC' fill-opacity='0.03'%3E%3Ccircle cx='7' cy='7' r='1'/%3E%3Ccircle cx='53' cy='7' r='1'/%3E%3Ccircle cx='30' cy='30' r='1'/%3E%3Ccircle cx='7' cy='53' r='1'/%3E%3Ccircle cx='53' cy='53' r='1'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
          }}
        ></div>
        
        {/* Floating Orbs */}
        <div className="absolute top-20 left-20 w-72 h-72 bg-success/10 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-20 right-20 w-96 h-96 bg-primary/10 rounded-full blur-3xl animate-pulse delay-1000"></div>
        
        <Header className="relative z-10" />
        
        <main className="relative z-10 pt-24 pb-12 px-6 lg:px-8">
          <div className="max-w-md mx-auto">
            <div className="bg-card/80 backdrop-blur-xl border border-border/50 rounded-2xl p-8 text-center animate-slide-up relative overflow-hidden">
              {/* Gradient Border Effect */}
              <div className="absolute inset-0 bg-gradient-to-r from-success/5 via-primary/5 to-success/5 rounded-2xl"></div>
              
              <div className="relative">
                <div className="mx-auto flex items-center justify-center h-20 w-20 rounded-full bg-gradient-to-br from-success to-success/80 mb-6 transform hover:scale-105 transition-transform duration-300">
                  <CheckCircle className="h-10 w-10 text-white animate-pulse" />
                  <div className="absolute -inset-1 bg-gradient-to-r from-success/20 to-primary/20 rounded-full blur-md"></div>
                </div>
                <h1 className="text-3xl font-bold text-foreground mb-4 bg-gradient-to-r from-success to-foreground bg-clip-text text-transparent">
                  Account created successfully!
                </h1>
                <p className="text-muted-foreground mb-8 leading-relaxed">
                  Please check your email to verify your account. You will be redirected to login shortly.
                </p>
                <div className="space-y-3">
                  <Button
                    asChild
                    fullWidth
                    size="lg"
                    className="bg-gradient-to-r from-success to-success/90 hover:from-success/90 hover:to-success border-0 transform hover:scale-[1.02] transition-all duration-200"
                  >
                    <Link to="/auth/login">
                      Go to Login
                    </Link>
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-muted/20 relative overflow-hidden">
      {/* Background Pattern */}
      <div 
        className="absolute inset-0 opacity-50" 
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%239C92AC' fill-opacity='0.03'%3E%3Ccircle cx='7' cy='7' r='1'/%3E%3Ccircle cx='53' cy='7' r='1'/%3E%3Ccircle cx='30' cy='30' r='1'/%3E%3Ccircle cx='7' cy='53' r='1'/%3E%3Ccircle cx='53' cy='53' r='1'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
        }}
      ></div>
      
      {/* Floating Orbs */}
      <div className="absolute top-20 left-20 w-72 h-72 bg-primary/10 rounded-full blur-3xl animate-pulse"></div>
      <div className="absolute bottom-20 right-20 w-96 h-96 bg-secondary/10 rounded-full blur-3xl animate-pulse delay-1000"></div>
      
      <Header className="relative z-10" />
      
      <main className="relative z-10 pt-24 pb-12 px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center min-h-[calc(100vh-12rem)]">
            
            {/* Left Side - Centered Text */}
            <div className="flex flex-col items-center justify-center text-center animate-fade-in">
              <h1 className="text-5xl font-bold text-foreground mb-6 bg-gradient-to-r from-foreground to-muted-foreground bg-clip-text text-transparent">
                Create your account
              </h1>
              <p className="text-xl text-muted-foreground leading-relaxed max-w-md">
                Join MessagingHub Pro and start building amazing messaging experiences
              </p>
            </div>

            {/* Right Side - Signup Form */}
            <div className="flex items-center justify-center">
              <div className="w-full max-w-md animate-slide-up">
                <div className="bg-card/90 border border-border/50 rounded-2xl p-8 relative overflow-hidden">
                  
                  <form className="space-y-6 relative" onSubmit={handleSubmit}>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <label htmlFor="firstName" className="block text-sm font-semibold text-foreground mb-2">
                          First Name
                        </label>
                        <div className="relative group">
                          <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                            <User className="h-5 w-5 text-muted-foreground group-hover:text-primary transition-colors" />
                          </div>
                          <input
                            id="firstName"
                            name="firstName"
                            type="text"
                            autoComplete="given-name"
                            required
                            value={formData?.firstName}
                            onChange={handleInputChange}
                            className="block w-full pl-12 pr-4 py-4 bg-background border border-border rounded-xl text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary/50 transition-all duration-200 hover:border-primary/30"
                            placeholder="First name"
                          />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <label htmlFor="lastName" className="block text-sm font-semibold text-foreground mb-2">
                          Last Name
                        </label>
                        <div className="relative group">
                          <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                            <User className="h-5 w-5 text-muted-foreground group-hover:text-primary transition-colors" />
                          </div>
                          <input
                            id="lastName"
                            name="lastName"
                            type="text"
                            autoComplete="family-name"
                            required
                            value={formData?.lastName}
                            onChange={handleInputChange}
                            className="block w-full pl-12 pr-4 py-4 bg-background border border-border rounded-xl text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary/50 transition-all duration-200 hover:border-primary/30"
                            placeholder="Last name"
                          />
                        </div>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <label htmlFor="companyName" className="block text-sm font-semibold text-foreground mb-2">
                        Company Name
                      </label>
                      <div className="relative group">
                        <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                          <Icon name="Building" size={20} className="text-muted-foreground group-hover:text-primary transition-colors" />
                        </div>
                        <input
                          id="companyName"
                          name="companyName"
                          type="text"
                          autoComplete="company-name"
                          required
                          value={formData?.companyName}
                          onChange={handleInputChange}
                          className="block w-full pl-12 pr-4 py-4 bg-background border border-border rounded-xl text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary/50 transition-all duration-200 hover:border-primary/30"
                          placeholder="Company name"
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <label htmlFor="email" className="block text-sm font-semibold text-foreground mb-2">
                        Email address
                      </label>
                      <div className="relative group">
                        <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                          <Mail className="h-5 w-5 text-muted-foreground group-hover:text-primary transition-colors" />
                        </div>
                        <input
                          id="email"
                          name="email"
                          type="email"
                          autoComplete="email"
                          required
                          value={formData?.email}
                          onChange={handleInputChange}
                          className="block w-full pl-12 pr-4 py-4 bg-background border border-border rounded-xl text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary/50 transition-all duration-200 hover:border-primary/30"
                          placeholder="Enter your email"
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <label htmlFor="password" className="block text-sm font-semibold text-foreground mb-2">
                        Password
                      </label>
                      <div className="relative group">
                        <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                          <Lock className="h-5 w-5 text-muted-foreground group-hover:text-primary transition-colors" />
                        </div>
                        <input
                          id="password"
                          name="password"
                          type={showPassword ? 'text' : 'password'}
                          autoComplete="new-password"
                          required
                          value={formData?.password}
                          onChange={handleInputChange}
                          className="block w-full pl-12 pr-14 py-4 bg-background border border-border rounded-xl text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary/50 transition-all duration-200 hover:border-primary/30"
                          placeholder="Create a password"
                        />
                        <button
                          type="button"
                          className="absolute inset-y-0 right-0 pr-4 flex items-center text-muted-foreground hover:text-primary transition-all duration-200 hover:scale-110"
                          onClick={() => setShowPassword(!showPassword)}
                        >
                          {showPassword ? (
                            <EyeOff className="h-5 w-5" />
                          ) : (
                            <Eye className="h-5 w-5" />
                          )}
                        </button>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <label htmlFor="confirmPassword" className="block text-sm font-semibold text-foreground mb-2">
                        Confirm password
                      </label>
                      <div className="relative group">
                        <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                          <Lock className="h-5 w-5 text-muted-foreground group-hover:text-primary transition-colors" />
                        </div>
                        <input
                          id="confirmPassword"
                          name="confirmPassword"
                          type={showConfirmPassword ? 'text' : 'password'}
                          autoComplete="new-password"
                          required
                          value={formData?.confirmPassword}
                          onChange={handleInputChange}
                          className="block w-full pl-12 pr-14 py-4 bg-background border border-border rounded-xl text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary/50 transition-all duration-200 hover:border-primary/30"
                          placeholder="Confirm your password"
                        />
                        <button
                          type="button"
                          className="absolute inset-y-0 right-0 pr-4 flex items-center text-muted-foreground hover:text-primary transition-all duration-200 hover:scale-110"
                          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        >
                          {showConfirmPassword ? (
                            <EyeOff className="h-5 w-5" />
                          ) : (
                            <Eye className="h-5 w-5" />
                          )}
                        </button>
                      </div>
                    </div>

                    {/* CAPTCHA */}
                    <div className="space-y-2">
                      <Captcha
                        onVerify={handleCaptchaVerify}
                        error={captchaError}
                        resetTrigger={captchaReset}
                      />
                    </div>

                    {error && (
                      <div className="flex items-center space-x-3 text-error bg-error/10 border border-error/20 p-4 rounded-xl animate-shake">
                        <AlertCircle className="h-5 w-5 flex-shrink-0" />
                        <span className="text-sm font-medium">{error}</span>
                      </div>
                    )}

                    <div className="pt-2">
                      <Button
                        type="submit"
                        disabled={loading}
                        loading={loading}
                        fullWidth
                        size="lg"
                        className="bg-gradient-to-r from-primary to-primary/90 hover:from-primary/90 hover:to-primary border-0 transform hover:scale-[1.02] transition-all duration-200"
                      >
                        {loading ? 'Creating account...' : 'Create account'}
                      </Button>
                    </div>
                  </form>

                  {/* Password Requirements */}
                  <div className="mt-8 pt-6 border-t border-border/30">
                    <div className="relative">
                      <div className="absolute inset-0 flex items-center">
                        <div className="w-full border-t border-border/30" />
                      </div>
                      <div className="relative flex justify-center text-sm">
                        <span className="px-4 bg-card/80 backdrop-blur-xl text-muted-foreground font-medium">Password requirements</span>
                      </div>
                    </div>
                    <div className="mt-4 bg-gradient-to-br from-muted/20 to-muted/10 rounded-xl p-5 backdrop-blur-sm border border-border/30">
                      <ul className="text-sm text-muted-foreground space-y-3">
                        <li className="flex items-center group">
                          <div className="w-6 h-6 bg-success/10 rounded-lg flex items-center justify-center mr-3 group-hover:bg-success/20 transition-colors">
                            <Icon name="Check" size={14} className="text-success" />
                          </div>
                          <span className="font-medium">At least 6 characters long</span>
                        </li>
                        <li className="flex items-center group">
                          <div className="w-6 h-6 bg-success/10 rounded-lg flex items-center justify-center mr-3 group-hover:bg-success/20 transition-colors">
                            <Icon name="Check" size={14} className="text-success" />
                          </div>
                          <span className="font-medium">Passwords must match</span>
                        </li>
                      </ul>
                    </div>
                  </div>

                  {/* Login Link */}
                  <div className="mt-6 text-center">
                    <span className="text-sm text-muted-foreground">
                      Already have an account?{' '}
                    </span>
                    <Link 
                      to="/auth/login" 
                      className="text-primary hover:text-primary/80 font-medium transition-all duration-200 hover:underline underline-offset-4 ml-1"
                    >
                      Sign in here
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default Signup;